/**
 * 각 번호의 출현 빈도를 계산합니다.
 * @param history - 로또 당첨 번호 이력 데이터 (2차원 배열)
 * @returns 각 번호의 출현 빈도를 담은 객체. 예: { '1': 3, '10': 5, ... }
 */
interface LottoDraw {
  drawNumber: number;
  drawDate: string;
  numbers: number[];
  bonusNumber?: number; // 보너스 번호는 선택적일 수 있음
}

/**
 * 각 번호의 출현 빈도를 계산합니다.
 * @param history - 로또 당첨 번호 이력 데이터 (객체 배열)
 * @returns 각 번호의 출현 빈도를 담은 객체. 예: { '1': 3, '10': 5, ... }
 */
export const calculateNumberFrequency = (
  history: LottoDraw[]
): { [key: number]: number } => {
  const frequency: { [key: number]: number } = {};

  for (let i = 1; i <= 45; i++) {
    frequency[i] = 0;
  }

  history.forEach((draw) => {
    draw.numbers.forEach((number) => {
      if (frequency[number] !== undefined) {
        frequency[number]++;
      }
    });
  });

  return frequency;
};

/**
 * 출현 빈도 데이터를 기반으로 추천 번호를 생성합니다.
 * @param frequency - 번호 출현 빈도 데이터
 * @returns 가장 많이 나온 숫자 15개 중에서 무작위로 6개를 조합하여 오름차순으로 정렬된 배열
 */
export const generateRecommendedNumbers = (
  frequency: { [key: number]: number }
): number[] => {
  // 빈도수를 기준으로 내림차순 정렬하여 상위 15개 번호(Hot Numbers)를 추출합니다.
  const hotNumbers = Object.entries(frequency)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 15)
    .map(([number]) => parseInt(number, 10));

  // Hot Numbers 중에서 6개를 무작위로 선택합니다.
  const recommendedNumbers: number[] = [];
  const numbersToPick = [...hotNumbers];

  while (recommendedNumbers.length < 6 && numbersToPick.length > 0) {
    const randomIndex = Math.floor(Math.random() * numbersToPick.length);
    const selectedNumber = numbersToPick.splice(randomIndex, 1)[0];
    recommendedNumbers.push(selectedNumber);
  }

  // 오름차순으로 정렬하여 반환합니다.
  return recommendedNumbers.sort((a, b) => a - b);
};

/**
 * 로또 번호에 따른 색상 클래스 이름을 반환합니다.
 * @param number - 로또 번호
 * @returns 색상에 해당하는 CSS 클래스 이름 (e.g., 'yellow', 'blue')
 */
export const getLottoNumberColor = (number: number): string => {
  if (number >= 1 && number <= 10) return 'yellow';
  if (number >= 11 && number <= 20) return 'blue';
  if (number >= 21 && number <= 30) return 'red';
  if (number >= 31 && number <= 40) return 'grey';
  if (number >= 41 && number <= 45) return 'green';
  return ''; // 기본값
};

// --- Helper Functions ---

/**
 * 주어진 배열에서 무작위로 n개의 요소를 추출합니다.
 * @param arr - 원본 배열
 * @param n - 추출할 요소의 개수
 * @returns 무작위로 추출된 요소들의 배열
 */
const getRandomElements = <T,>(arr: T[], n: number): T[] => {
  const result: T[] = [];
  const source = [...arr];
  while (result.length < n && source.length > 0) {
    const randomIndex = Math.floor(Math.random() * source.length);
    result.push(source.splice(randomIndex, 1)[0]);
  }
  return result;
};

/**
 * 번호 빈도 객체를 정렬된 번호 배열로 변환합니다.
 * @param frequency - 번호 빈도 객체
 * @returns 빈도 순으로 정렬된 번호 배열
 */
const getSortedNumbersByFrequency = (frequency: { [key: number]: number }): number[] => {
  return Object.entries(frequency)
    .sort(([, a], [, b]) => b - a)
    .map(([number]) => parseInt(number, 10));
};

// --- Generation Strategies ---

/**
 * Hot Number 3개와 나머지 번호 3개를 조합하여 생성합니다.
 */
export const generateHotAndWild = (
  frequency: { [key: number]: number }
): number[] => {
  const sortedNumbers = getSortedNumbersByFrequency(frequency);
  const hotNumbers = sortedNumbers.slice(0, 15);
  const wildNumbers = sortedNumbers.slice(15);

  const selectedHot = getRandomElements(hotNumbers, 3);
  const selectedWild = getRandomElements(wildNumbers, 3);

  const finalNumbers = [...selectedHot, ...selectedWild];
  return finalNumbers.sort((a, b) => a - b);
};

/**
 * Hot, Medium, Cold 그룹에서 각각 2개씩 조합하여 생성합니다.
 */
export const generateBalanced = (
  frequency: { [key: number]: number }
): number[] => {
  const sortedNumbers = getSortedNumbersByFrequency(frequency);
  const hotNumbers = sortedNumbers.slice(0, 15);
  const mediumNumbers = sortedNumbers.slice(15, 30);
  const coldNumbers = sortedNumbers.slice(30);

  const selectedHot = getRandomElements(hotNumbers, 2);
  const selectedMedium = getRandomElements(mediumNumbers, 2);
  const selectedCold = getRandomElements(coldNumbers, 2);

  const finalNumbers = [...selectedHot, ...selectedMedium, ...selectedCold];
  return finalNumbers.sort((a, b) => a - b);
};

/**
 * Hot Number 2개, 연속 번호 2개, 무작위 번호 2개를 조합하여 생성합니다.
 */
export const generatePatternBased = (
  frequency: { [key: number]: number }
): number[] => {
  const sortedNumbers = getSortedNumbersByFrequency(frequency);
  const hotNumbers = sortedNumbers.slice(0, 15);
  const allNumbers = Array.from({ length: 45 }, (_, i) => i + 1);

  const finalNumbers: number[] = [];

  // 1. Hot Number 2개 선택
  const selectedHot = getRandomElements(hotNumbers, 2);
  finalNumbers.push(...selectedHot);

  // 2. 연속 번호 2개 생성
  let consecutivePair: number[] = [];
  while (consecutivePair.length < 2) {
    const start = Math.floor(Math.random() * 44) + 1; // 1 ~ 44
    const pair = [start, start + 1];
    if (!finalNumbers.includes(pair[0]) && !finalNumbers.includes(pair[1])) {
      consecutivePair = pair;
    }
  }
  finalNumbers.push(...consecutivePair);

  // 3. 나머지 무작위 번호 2개 선택
  const remainingNumbers = allNumbers.filter(n => !finalNumbers.includes(n));
  const selectedRandom = getRandomElements(remainingNumbers, 2);
  finalNumbers.push(...selectedRandom);

  return finalNumbers.sort((a, b) => a - b);
};

// --- New Pattern-Filtered Generation ---

interface LottoData {
  drawNumber: number;
  numbers: number[];
  stats: {
    totalSum: number;
    oddEvenRatio: string;
  };
}

/**
 * 가장 흔한 홀짝 비율을 찾습니다.
 * @param data - 전체 로또 데이터
 * @returns 가장 많이 나온 홀짝 비율 (예: '3:3')
 */
const getMostFrequentOddEvenRatio = (data: LottoData[]): string => {
  const ratioCounts = data.reduce((acc, draw) => {
    const ratio = draw.stats.oddEvenRatio;
    acc[ratio] = (acc[ratio] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return Object.keys(ratioCounts).reduce((a, b) =>
    ratioCounts[a] > ratioCounts[b] ? a : b
  );
};

/**
 * 가장 흔한 총합 구간을 찾습니다. 구간은 40 단위로 나눕니다.
 * @param data - 전체 로또 데이터
 * @returns 가장 흔한 총합 구간 (예: { min: 121, max: 160 })
 */
const getMostFrequentSumRange = (
  data: LottoData[]
): { min: number; max: number } => {
  const rangeCounts: Record<string, number> = {};
  data.forEach(draw => {
    const rangeStart = Math.floor((draw.stats.totalSum - 1) / 40) * 40 + 1;
    const rangeEnd = rangeStart + 39;
    const rangeKey = `${rangeStart}-${rangeEnd}`;
    rangeCounts[rangeKey] = (rangeCounts[rangeKey] || 0) + 1;
  });

  const mostFrequentKey = Object.keys(rangeCounts).reduce((a, b) =>
    rangeCounts[a] > rangeCounts[b] ? a : b
  );

  const [min, max] = mostFrequentKey.split('-').map(Number);
  return { min, max };
};

/**
 * 주어진 숫자 배열의 홀짝 비율을 계산합니다.
 * @param numbers - 숫자 배열
 * @returns 홀짝 비율 문자열 (예: '3:3')
 */
const getOddEvenRatio = (numbers: number[]): string => {
  const oddCount = numbers.filter(n => n % 2 !== 0).length;
  const evenCount = 6 - oddCount;
  return `${oddCount}:${evenCount}`;
};

/**
 * 통계적 패턴(홀짝 비율, 총합)을 기반으로 로또 번호를 생성합니다.
 * @param history - 전체 로또 데이터
 * @returns 조건을 만족하는 6개의 정렬된 로또 번호
 */
export const generatePatternFilteredNumbers = (history: LottoData[]): number[] => {
  const mostFrequentRatio = getMostFrequentOddEvenRatio(history);
  const { min: minSum, max: maxSum } = getMostFrequentSumRange(history);

  while (true) {
    // 1. 1~45 사이의 무작위 번호 6개 생성
    const generatedNumbers: Set<number> = new Set();
    while (generatedNumbers.size < 6) {
      const randomNumber = Math.floor(Math.random() * 45) + 1;
      generatedNumbers.add(randomNumber);
    }
    const currentNumbers = Array.from(generatedNumbers);

    // 2. 조건 확인
    const currentRatio = getOddEvenRatio(currentNumbers);
    const currentSum = currentNumbers.reduce((sum, n) => sum + n, 0);

    // 3. 조건 만족 시 반환
    if (currentRatio === mostFrequentRatio && currentSum >= minSum && currentSum <= maxSum) {
      return currentNumbers.sort((a, b) => a - b);
    }
  }
};

/**
 * 1~45 사이의 숫자 중 무작위로 6개를 선택하여 정렬된 배열로 반환합니다.
 * @returns 6개의 무작위 로또 번호 배열
 */
export const generatePurelyRandom = (): number[] => {
  const numbers = new Set<number>();
  while (numbers.size < 6) {
    const randomNumber = Math.floor(Math.random() * 45) + 1;
    numbers.add(randomNumber);
  }
  return Array.from(numbers).sort((a, b) => a - b);
};

/**
 * 현재 날짜를 기준으로 다음 로또 추첨 회차를 계산합니다.
 * @returns {number} 다음 추첨 회차 번호
 */
export const getNextDrawNumber = (): number => {
  const firstDrawDate = new Date('2002-12-07T21:00:00+09:00');
  const now = new Date();
  const diffTime = now.getTime() - firstDrawDate.getTime();
  const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
  return diffWeeks + 2; // 현재 주 + 1
};

/**
 * 생성된 번호와 당첨 번호를 비교하여 등수를 반환합니다.
 * @param userNumbers - 사용자가 생성한 번호 배열
 * @param winningDraw - 해당 회차의 당첨 결과 객체
 * @returns {string} 등수 (예: "1등", "2등", "낙첨")
 */
export const getRank = (userNumbers: number[], winningDraw: LottoDraw | undefined): string => {
  if (!winningDraw) {
    return "추첨 대기";
  }

  const winningNumbers = new Set(winningDraw.numbers);
  const bonusNumber = winningDraw.bonusNumber;

  const matchCount = userNumbers.filter(num => winningNumbers.has(num)).length;
  const bonusMatch = bonusNumber ? userNumbers.includes(bonusNumber) : false;

  if (matchCount === 6) return "1등";
  if (matchCount === 5 && bonusMatch) return "2등";
  if (matchCount === 5) return "3등";
  if (matchCount === 4) return "4등";
  if (matchCount === 3) return "5등";

  return "낙첨";
};

// --- 로또 번호 비교 분석 함수 ---

/**
 * 비교 분석을 위한 이력 데이터의 타입 정의
 */
export interface HistoryData {
  drawNumber: number;
  numbers: number[];
}

/**
 * 비교 분석 결과 객체의 타입 정의
 */
export interface ComparisonResult {
  bestMatch: {
    count: number;
    drawNumber: number | null;
  };
  fiveMatchCount: number;
  fourMatchCount: number;
  threeMatchCount: number;
}

/**
 * 생성된 번호를 전체 당첨 이력과 비교하여 통계를 분석합니다.
 * @param generatedNumbers - 사용자가 생성한 6개의 숫자 배열
 * @param historyData - 전체 로또 당첨 이력 데이터
 * @returns 분석 결과 객체
 */
export const compareWithHistory = (
  generatedNumbers: number[],
  historyData: HistoryData[]
): ComparisonResult => {
  let bestMatchCount = 0;
  let bestMatchDrawNumber: number | null = null;
  let fiveMatchCount = 0;
  let fourMatchCount = 0;
  let threeMatchCount = 0;

  const generatedNumbersSet = new Set(generatedNumbers);

  for (const draw of historyData) {
    const matchCount = draw.numbers.filter((num) =>
      generatedNumbersSet.has(num)
    ).length;

    if (matchCount > bestMatchCount) {
      bestMatchCount = matchCount;
      bestMatchDrawNumber = draw.drawNumber;
    }

    switch (matchCount) {
      case 3:
        threeMatchCount++;
        break;
      case 4:
        fourMatchCount++;
        break;
      case 5:
        fiveMatchCount++;
        break;
      default:
        break;
    }
  }

  return {
    bestMatch: { count: bestMatchCount, drawNumber: bestMatchDrawNumber },
    fiveMatchCount,
    fourMatchCount,
    threeMatchCount,
  };
};

// --- 로또 번호 패턴 분석 함수 ---

/**
 * 주어진 번호 배열의 패턴(세로/가로줄 집중)을 분석하여 코멘트를 반환합니다.
 * @param numbers - 사용자가 생성한 6개의 숫자 배열
 * @returns 패턴 분석 결과 코멘트 문자열
 */
export const analyzeLottoPattern = (numbers: number[]): string => {
  const getColumn = (n: number) => (n - 1) % 7;
  const getRow = (n: number) => Math.floor((n - 1) / 7);

  const columnCounts: { [key: number]: number } = {};
  const rowCounts: { [key: number]: number } = {};

  for (const num of numbers) {
    const col = getColumn(num);
    const row = getRow(num);
    columnCounts[col] = (columnCounts[col] || 0) + 1;
    rowCounts[row] = (rowCounts[row] || 0) + 1;
  }

  for (const count of Object.values(columnCounts)) {
    if (count >= 3) {
      return '세로줄에 번호가 집중되었네요! 한 줄로 찍는 것도 전략이죠! 🎯';
    }
  }

  for (const count of Object.values(rowCounts)) {
    if (count >= 3) {
      return '가로줄 패턴이 발견되었습니다! 안정적인 조합이네요. 📏';
    }
  }

  return '숫자들이 골고루 분포되어 있네요. 균형 잡힌 조합입니다! ⚖️';
};

/*
--- 추가 패턴 분석 아이디어 ---

1.  **대각선 패턴 분석:**
    - 로또 용지에서 좌상단 -> 우하단 대각선은 (행 - 열) 값이 일정합니다.
    - 우상단 -> 좌하단 대각선은 (행 + 열) 값이 일정합니다.
    - 이 값을 기준으로 카운트를 세어 3개 이상이면 대각선 패턴으로 판단할 수 있습니다.

    const diag1Counts: { [key: number]: number } = {}; // row - col
    const diag2Counts: { [key: number]: number } = {}; // row + col

    for (const num of numbers) {
      const row = getRow(num);
      const col = getColumn(num);
      diag1Counts[row - col] = (diag1Counts[row - col] || 0) + 1;
      diag2Counts[row + col] = (diag2Counts[row + col] || 0) + 1;
    }
    // 이후 로직은 세로/가로줄 분석과 동일

2.  **계단 모양 패턴 (연속성 분석):**
    - 번호를 정렬한 뒤, 인접한 번호들의 차이를 확인합니다.
    - 차이가 1인 경우가 2번 이상 연속되면 (예: 8, 9, 10) 가로 계단 패턴입니다.
    - 차이가 7인 경우가 2번 이상 연속되면 (예: 8, 15, 22) 세로 계단 패턴입니다.
    - 차이가 6 또는 8인 경우가 2번 이상 연속되면 대각선 계단 패턴입니다.

    const sorted = [...numbers].sort((a, b) => a - b);
    for (let i = 0; i < sorted.length - 2; i++) {
      const diff1 = sorted[i+1] - sorted[i];
      const diff2 = sorted[i+2] - sorted[i+1];
      if (diff1 === diff2 && [1, 6, 7, 8].includes(diff1)) {
        return '계단 모양 패턴이 발견되었네요! 📈';
      }
    }
*/
